import { PRODUCTS_PER_PAGE } from './const'

// Keep the server offset when the same Redux list is reused after a product visit.
const offsets = new WeakMap()

export const carryWorkwearPaginationOffset = (previous, next) => {
  if (offsets.has(previous)) offsets.set(next, offsets.get(previous))
  return next
}

export const getWorkwearPaginationKey = (props, startPageNo) =>
  JSON.stringify({
    categoryId: props.category?.id_category,
    filter: props.filter,
    sort: props.sort,
    internationalization: props.internationalization,
    customerId: props.customerId,
    view: props.view,
    startPageNo,
  })

const matches = (a, b) =>
  a?.key === b.key &&
  a?.payload === b.payload &&
  a?.enabled === b.enabled &&
  !b.fetching

export default function createWorkwearPagination({
  getContext,
  fetchPage,
  append,
  onStatus,
  onLoaded,
}) {
  let context
  let active
  let nextSkip
  let status = 'idle'
  let disposed = false

  const setStatus = (value) => {
    status = value
    onStatus(value)
  }

  const sync = () => {
    const current = getContext()
    if (!matches(context, current)) {
      context = current
      active = null
      nextSkip = current.payload && offsets.get(current.payload)
      if (status !== 'idle') setStatus('idle')
    }
    return current
  }

  const load = async (retry = false) => {
    if (disposed) return
    const current = sync()
    if (
      !current.enabled ||
      current.fetching ||
      active ||
      status === 'done' ||
      (status === 'error' && !retry) ||
      !current.payload?.products?.length
    )
      return

    const skip =
      nextSkip ??
      (current.startPageNo -
        1 +
        Math.ceil(current.payload.products.length / PRODUCTS_PER_PAGE)) *
        PRODUCTS_PER_PAGE
    const request = { current, skip }
    active = request
    setStatus(retry ? 'retrying' : 'loading')
    let added
    try {
      const response = await fetchPage(current, skip)
      if (disposed || active !== request) return
      if (!matches(current, getContext())) {
        sync()
        return
      }
      if (
        response?.error ||
        response?.ok === false ||
        !Array.isArray(response?.products) ||
        response.products.some((product) => product?.id_product == null)
      ) {
        throw new Error('Invalid product page')
      }

      const ids = new Set(
        current.payload.products.map((product) => String(product.id_product)),
      )
      added = response.products.filter((product) => {
        const id = String(product.id_product)
        if (ids.has(id)) return false
        ids.add(id)
        return true
      })
      if (response.products.length && !added.length)
        throw new Error('Repeated product page')

      if (added.length) {
        const payload = {
          ...current.payload,
          ...response,
          products: [...current.payload.products, ...added],
        }
        // The API pages by the requested limit. A partial page must not
        // request an offset within that same page and repeat its products.
        nextSkip = skip + Math.max(PRODUCTS_PER_PAGE, response.products.length)
        offsets.set(payload, nextSkip)
        context = { ...current, payload }
        append(payload)
      }
      setStatus(response.products.length ? 'idle' : 'done')
    } catch (error) {
      added = null
      if (!disposed && active === request && matches(current, getContext()))
        setStatus('error')
    } finally {
      if (active === request) active = null
    }
    // Tracking happens only after the successful append, never on a failed attempt.
    if (added?.length) onLoaded(current, skip, added)
  }

  return {
    load,
    sync,
    dispose: () => {
      disposed = true
      active = null
    },
  }
}
